/**
 * The bell in the top bar, and the panel behind it.
 *
 * ## What a click does
 *
 * Marks the row read and goes where the row points — switching the active brand first, because a
 * notification about a post on another brand is exactly the case where landing on the planner
 * would otherwise show somebody a calendar that does not contain the post they were told about.
 * The mark is fired and not awaited: the navigation is what the person asked for, and making it
 * wait on a write nobody is looking at would be a visible pause for nothing.
 *
 * ## Read is a fact, not a style
 *
 * An unread row carries a dot and a weight. It keeps them until the mark-read lands and the feed
 * re-reads, rather than being greyed out optimistically — a list that changes under a click that
 * then fails is worse than one that changes a beat later.
 *
 * ## Why there is no "see all" page
 *
 * The panel pages. A notification feed is read from the top and abandoned, not browsed, and a
 * whole route for the same twenty rows would be a second place to keep working.
 */

import { useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'
import { NOTIFICATION_PAGE_SIZE } from '@ftk-connector/shared'
import type { NotificationView } from '@ftk-connector/shared'

import { Icon } from '../../components/ui/Icon'
import { Popover } from '../../components/ui/Popover'
import { Spinner } from '../../components/ui/Spinner'
import { useToast } from '../../components/ui/Toast'
import { useAuth } from '../../lib/auth'
import { cn } from '../../lib/cn'
import { useFormat, useT } from '../../lib/i18n'
import type { SimpleTKey } from '../../lib/i18n'
import { useMarkAllRead, useMarkRead, useNotifications, useUnreadCount } from './api'

/**
 * `(type, decision)` → the sentence.
 *
 * An explicit map rather than a template: `t()` keys are never built at runtime in this codebase
 * (`frontend/src/lib/i18n/check.mjs` says why — the unused-key scan looks for quoted literals),
 * and `REVIEW_DECIDED` is two different sentences anyway.
 */
const sentenceKey = (row: NotificationView): SimpleTKey => {
  if (row.type === 'POST_ASSIGNED') return 'notifications.row.postAssigned'
  if (row.type === 'APPROVAL_REQUESTED') return 'notifications.row.approvalRequested'
  if (row.type === 'REVIEW_DECIDED') {
    return row.data['decision'] === 'REJECTED'
      ? 'notifications.row.reviewRejected'
      : 'notifications.row.reviewApproved'
  }
  if (row.type === 'FLOW_NOTIFIED') return 'notifications.row.flowNotified'
  if (row.type === 'AUTOMATION_NOTIFIED') return 'notifications.row.automationNotified'
  if (row.type === 'CONNECTION_NEEDS_ATTENTION') {
    /* One broken connection names the network; several name the count, because "Instagram needs
       reconnecting" would be a third of the truth when the whole Meta grant went at once. */
    return row.data['count'] === '1'
      ? 'notifications.row.connectionNeedsAttention'
      : 'notifications.row.connectionsNeedAttention'
  }
  if (row.type === 'BRAND_GONE_QUIET') return 'notifications.row.brandGoneQuiet'
  /* A type this build does not know — a row written by a newer deploy. The feed still renders it
     rather than dropping it: a person who can see a gap in their history cannot trust the list. */
  return 'notifications.row.generic'
}

const NotificationRow = ({
  row,
  onOpen,
}: {
  row: NotificationView
  onOpen: (row: NotificationView) => void
}): ReactElement => {
  const t = useT()
  const f = useFormat()
  const unread = row.readAt === null

  return (
    <button
      type="button"
      onClick={() => {
        onOpen(row)
      }}
      className={cn(
        'ring-focus flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors',
        'hover:bg-chrome-700',
      )}
    >
      <span
        aria-hidden={!unread}
        aria-label={unread ? t('notifications.panel.unreadDot') : undefined}
        className={cn(
          'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
          unread ? 'bg-brass' : 'bg-transparent',
        )}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            't-small block text-chrome-ink',
            unread ? 'font-semibold' : 'font-normal text-chrome-mute',
          )}
        >
          {t(sentenceKey(row), {
            actorName: row.data['actorName'] ?? '—',
            reviewerName: row.data['reviewerName'] ?? '—',
            /* A `FLOW_NOTIFIED` row names the rule that fired rather than a person, because none
               was involved. Every key here is supplied to every sentence — the renderer uses what
               its own string asks for — which is why one row's missing key is a dash and not a gap. */
            flowName: row.data['flowName'] ?? '—',
            ruleName: row.data['ruleName'] ?? '—',
            /* The nudges name a network, a count and a number of days instead of a person —
               nobody acted, which is the whole point of them. */
            network: row.data['network'] ?? '—',
            count: row.data['count'] ?? '—',
            days: row.data['days'] ?? '—',
            brandName: row.link.brandName ?? row.data['brandName'] ?? '—',
          })}
        </span>
        {row.data['excerpt'] ? (
          <span className="t-small mt-0.5 block truncate text-chrome-mute">
            {row.data['excerpt']}
          </span>
        ) : null}
        <span className="t-label mt-1 block text-chrome-mute">{f.dateTime(row.createdAt)}</span>
      </span>
    </button>
  )
}

export const NotificationBell = (): ReactElement | null => {
  const t = useT()
  const navigate = useNavigate()
  const { status, switchBrand } = useAuth()
  const { toast } = useToast()
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)

  const unreadQuery = useUnreadCount()
  const feedQuery = useNotifications(page, open)
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  if (status !== 'authenticated') return null

  const unread = unreadQuery.data?.unread ?? 0
  const capped = unreadQuery.data?.capped ?? false
  const rows = feedQuery.data?.rows ?? []
  const total = feedQuery.data?.total ?? 0

  const openRow = (row: NotificationView): void => {
    setOpen(false)
    if (row.readAt === null) {
      /* Fired, not awaited: the navigation below is what was asked for. A failure is a row that
         stays bold, which the next poll corrects. */
      markRead.mutate(row.id)
    }
    if (row.link.plannerPostId === null) return
    /* The brand first — a planner opened on the wrong brand would not contain this post. */
    if (row.link.blogId !== null) switchBrand(row.link.blogId)
    void navigate(`/app/planner?post=${String(row.link.plannerPostId)}`)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          setPage(1)
          setOpen((previous) => !previous)
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          unread > 0
            ? t('notifications.panel.openWithUnread', { count: unread })
            : t('notifications.panel.open')
        }
        className="ring-focus relative flex h-9 w-9 items-center justify-center rounded-sm text-chrome-mute transition-colors hover:bg-chrome-800 hover:text-chrome-ink"
      >
        <Icon name="bell" size={18} />
        {unread > 0 ? (
          <span
            aria-hidden
            className="t-label absolute right-0.5 top-0.5 min-w-4 rounded-full bg-brass px-1 text-center leading-4 text-chrome-900"
          >
            {capped ? t('notifications.badge.overflow', { count: unread }) : unread}
          </span>
        ) : null}
      </button>

      <Popover
        open={open}
        onClose={() => {
          setOpen(false)
        }}
        anchorRef={triggerRef}
        placement="bottom"
        align="end"
        surface="chrome"
        label={t('notifications.panel.title')}
        className="w-88 max-w-[calc(100vw-2rem)] py-1"
      >
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <p className="t-label text-chrome-mute">{t('notifications.panel.title')}</p>
          {unread > 0 ? (
            <button
              type="button"
              className="t-small ring-focus rounded-sm px-1 text-brass hover:underline"
              disabled={markAllRead.isPending}
              onClick={() => {
                markAllRead.mutate(undefined, {
                  onSuccess: ({ marked }) => {
                    toast({ title: t('notifications.panel.allRead', { count: marked }), tone: 'success' })
                  },
                })
              }}
            >
              {t('notifications.panel.markAllRead')}
            </button>
          ) : null}
        </div>

        <div className="max-h-96 overflow-y-auto border-t border-chrome-600">
          {feedQuery.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : feedQuery.isError ? (
            <p className="t-small px-3 py-6 text-center text-chrome-mute">
              {t('notifications.panel.loadError')}
            </p>
          ) : rows.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <p className="t-small font-semibold text-chrome-ink">
                {t('notifications.panel.emptyTitle')}
              </p>
              <p className="t-small mt-1 text-chrome-mute">{t('notifications.panel.emptyBody')}</p>
            </div>
          ) : (
            rows.map((row) => <NotificationRow key={row.id} row={row} onOpen={openRow} />)
          )}
        </div>

        {/* Pages rather than an ever-growing list: a feed is read from the top and abandoned, and
            twenty rows is already more than anybody scrolls past in a dropdown. The buttons are
            only drawn when there is somewhere to go. */}
        {page > 1 || total > page * NOTIFICATION_PAGE_SIZE ? (
          <div className="flex items-center justify-between border-t border-chrome-600 px-3 py-2">
            <button
              type="button"
              disabled={page === 1}
              className="t-small ring-focus rounded-sm px-1 text-brass hover:underline disabled:invisible"
              onClick={() => {
                setPage((previous) => Math.max(1, previous - 1))
              }}
            >
              {t('notifications.panel.newer')}
            </button>
            <button
              type="button"
              disabled={total <= page * NOTIFICATION_PAGE_SIZE}
              className="t-small ring-focus rounded-sm px-1 text-brass hover:underline disabled:invisible"
              onClick={() => {
                setPage((previous) => previous + 1)
              }}
            >
              {t('notifications.panel.older')}
            </button>
          </div>
        ) : null}

        <button
          type="button"
          className="t-small ring-focus w-full border-t border-chrome-600 py-2 text-center text-chrome-mute hover:text-chrome-ink"
          onClick={() => {
            setOpen(false)
            void navigate('/app/settings')
          }}
        >
          {t('notifications.panel.settings')}
        </button>
      </Popover>
    </>
  )
}
